import { useEffect, useState } from "react";
import { UserAvatar } from "@/components/UserAvatar";
import { Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, ChevronDown, ChevronRight, Building2, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_STYLES } from "@/lib/session";

const emptyLibForm = { category: CATEGORIES[0], subcategory: "", title: "", body: "", default_duration_minutes: 5 };

function AdminMinistryLibrary({ ministryId }) {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyLibForm);
  const [saving, setSaving] = useState(false);

  const loadItems = () => {
    api.get("/ministry-library", { params: { ministry_id: ministryId } }).then(({ data }) => setItems(data));
  };

  useEffect(() => { loadItems(); }, [ministryId]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyLibForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({ category: item.category, subcategory: item.subcategory || "", title: item.title, body: item.body, default_duration_minutes: item.default_duration_minutes });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/ministry-library/${editingItem.id}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/ministry-library", { ministry_id: ministryId, ...form });
        toast.success("Item added");
      }
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from this ministry's private library?`)) return;
    await api.delete(`/ministry-library/${item.id}`);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    toast.success("Item deleted");
  };

  return (
    <div data-testid={`admin-ministry-library-${ministryId}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Private Library ({items.length})</p>
        <Button size="sm" variant="outline" onClick={openCreate} data-testid={`admin-add-ministry-item-${ministryId}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
        </Button>
      </div>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No private library items yet</p>}
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-2 py-1.5 rounded-md bg-white dark:bg-[var(--brand-card-dark)]" data-testid={`admin-ministry-item-${item.id}`}>
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className={CATEGORY_STYLES[item.category]?.badge}>{item.category}</Badge>
              <span>{item.title}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(item)} className="text-muted-foreground hover:text-foreground" data-testid={`admin-edit-ministry-item-${item.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => handleDelete(item)} className="text-muted-foreground hover:text-[var(--brand-crimson)]" data-testid={`admin-delete-ministry-item-${item.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="admin-ministry-item-form-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingItem ? "Edit item" : "Add private library item"}</DialogTitle>
            <DialogDescription>Site Admin override — this affects this ministry's private library only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="admin-ministry-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subcategory</Label>
              <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="admin-ministry-item-subcategory-input" />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-ministry-item-title-input" />
            </div>
            <div>
              <Label>Text</Label>
              <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="admin-ministry-item-body-input" />
            </div>
            <div>
              <Label>Default duration (minutes)</Label>
              <Input type="number" min={1} value={form.default_duration_minutes} onChange={(e) => setForm({ ...form, default_duration_minutes: Number(e.target.value) || 1 })} data-testid="admin-ministry-item-duration-input" />
            </div>
            <Button onClick={submit} disabled={saving || !form.title.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="admin-ministry-item-submit-button">
              {saving ? "Saving..." : editingItem ? "Save changes" : "Add item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MinistryRow({ ministry }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);

  const toggle = () => {
    if (!expanded && !detail) {
      api.get(`/orgs/ministries/${ministry.id}`).then(({ data }) => setDetail(data));
    }
    setExpanded(!expanded);
  };

  return (
    <div className="border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg overflow-hidden" data-testid={`admin-ministry-${ministry.id}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-[var(--brand-card-dark)] hover:bg-[var(--brand-bg)] dark:hover:bg-[var(--brand-bg-dark)]" data-testid={`admin-ministry-toggle-${ministry.id}`}>
        <span className="font-medium text-sm">{ministry.name}</span>
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {expanded && (
        <div className="px-4 py-3 bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)] space-y-1.5">
          {detail?.members?.length ? (
            detail.members.map((m) => (
              <div key={m.email} className="flex items-center gap-2 text-sm" data-testid={`admin-member-${m.email}`}>
                <UserAvatar name={m.name} email={m.email} picture={m.picture} className="h-6 w-6 text-[10px]" />
                <span>{m.name ? `${m.name} · ${m.email}` : m.email}</span>
                <Badge variant="outline">{m.role}</Badge>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No members yet</p>
          )}
          <button
            onClick={() => setShowLibrary((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--brand-gold-text)] dark:text-amber-300 hover:underline pt-2"
            data-testid={`admin-toggle-library-${ministry.id}`}
          >
            <BookOpen className="h-3.5 w-3.5" /> {showLibrary ? "Hide" : "Manage"} private library
          </button>
          {showLibrary && (
            <div className="pt-2 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] mt-2">
              <AdminMinistryLibrary ministryId={ministry.id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrganizationsAdmin() {
  const { user } = useAuth();
  const [parishes, setParishes] = useState([]);
  const [selectedParish, setSelectedParish] = useState(null);
  const [ministries, setMinistries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newParishName, setNewParishName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadParishes = () => {
    api.get("/orgs/parishes").then(({ data }) => {
      setParishes(data);
      if (data.length > 0 && !selectedParish) setSelectedParish(data[0].id);
    });
  };

  useEffect(() => {
    loadParishes();
  }, []);

  useEffect(() => {
    if (!selectedParish) {
      setMinistries([]);
      return;
    }
    api.get(`/orgs/parishes/${selectedParish}/ministries`).then(({ data }) => setMinistries(data));
  }, [selectedParish]);

  if (user && user.role !== "admin") {
    return <Navigate to="/sessions" replace />;
  }

  const handleCreateParish = async () => {
    if (!newParishName.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/orgs/parishes", { name: newParishName.trim() });
      toast.success("Parish created");
      setDialogOpen(false);
      setNewParishName("");
      loadParishes();
      setSelectedParish(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create parish");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParish = async (id) => {
    if (!window.confirm("Delete this parish and all its ministries?")) return;
    await api.delete(`/orgs/parishes/${id}`);
    toast.success("Parish deleted");
    setSelectedParish(null);
    loadParishes();
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100">Organizations</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage parishes and view every ministry's roster</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="create-parish-button">
            <Plus className="h-4 w-4 mr-2" /> New Parish
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-6">
          <div className="space-y-2" data-testid="parish-list">
            {parishes.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedParish(p.id)}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer ${selectedParish === p.id ? "border-[var(--brand-navy)] bg-white dark:bg-[var(--brand-card-dark)]" : "border-[var(--brand-border)] dark:border-[var(--brand-border-dark)]"}`}
                data-testid={`parish-card-${p.id}`}
              >
                <span className="flex items-center gap-2 text-sm font-medium"><Building2 className="h-3.5 w-3.5 shrink-0" /> {p.name}</span>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteParish(p.id); }} className="text-muted-foreground hover:text-[var(--brand-crimson)]" data-testid={`delete-parish-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {parishes.length === 0 && <p className="text-sm text-muted-foreground">No parishes yet</p>}
          </div>

          <div className="sm:col-span-2 space-y-3" data-testid="ministries-panel">
            {ministries.map((m) => <MinistryRow key={m.id} ministry={m} />)}
            {selectedParish && ministries.length === 0 && <p className="text-sm text-muted-foreground">No ministries in this parish yet — Ministry Heads can self-create one under "My Ministry".</p>}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="create-parish-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New Parish</DialogTitle>
            <DialogDescription>Create a new parish organization on the platform.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={newParishName} onChange={(e) => setNewParishName(e.target.value)} placeholder="e.g. The Church of the Holy Family (Katong)" data-testid="new-parish-name-input" />
            <Button onClick={handleCreateParish} disabled={saving || !newParishName.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="submit-create-parish-button">
              {saving ? "Creating..." : "Create Parish"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
